import { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { MealQuantityChild } from '@/types/Meal'
import { ChildCareOutlined } from '@mui/icons-material'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import styles from './ShowMealChildrenDialog.module.scss'

interface ShowMealChildrenDialogProps {
    open: boolean
    handleClose: () => void
    childs: MealQuantityChild[]
    title: string
}

type MealQuantityChildGrouped = MealQuantityChild & { count: number }

function groupChildrenById(childs: MealQuantityChild[]): MealQuantityChildGrouped[] {
    const map: Map<number, MealQuantityChildGrouped> = new Map()
    for (const child of childs) {
        const existing: MealQuantityChildGrouped | undefined = map.get(child.childId)
        if (existing) {
            existing.count += 1
        } else {
            map.set(child.childId, { ...child, count: 1 })
        }
    }
    return [...map.values()]
}

const ShowMealChildrenDialog: FunctionComponent<ShowMealChildrenDialogProps> = ({
    open,
    handleClose,
    childs,
    title
}: ShowMealChildrenDialogProps) => {
    const { t } = useTranslation()
    const navigate: NavigateFunction = useNavigate()

    const groupedChilds: MealQuantityChildGrouped[] = groupChildrenById(childs)

    return (
        <Dialog open={open} handleClose={handleClose} className={styles.Dialog}>
            <DialogHeader title={title} />
            <DialogBody className={styles.DialogBody}>
                {groupedChilds.length > 0 && (
                    <TableContainer className={styles.TablePlacement}>
                        <Table className={styles.Table}>
                            <TableHead className={styles.TableHeader}>
                                <TableRow>
                                    <TableCell>{t('child')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...groupedChilds]
                                    .sort((a: MealQuantityChildGrouped, b: MealQuantityChildGrouped) =>
                                        a.firstname.localeCompare(b.firstname)
                                    )
                                    .map((child: MealQuantityChildGrouped) => (
                                        <TableRow key={child.childId}>
                                            <TableCell className={styles.TextIcon}>
                                                <ChildCareOutlined
                                                    onClick={() => navigate('/childs?child=' + child.childId)}
                                                    className={styles.Icon}
                                                />
                                                <p className={styles.Text}>
                                                    {child.firstname}
                                                    {child.count > 1 ? ` (x${child.count})` : ''}
                                                </p>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type='close' onClick={handleClose} className={styles.Button} />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default ShowMealChildrenDialog
